import { useMemo } from "react";
import { motion } from "framer-motion";

// Lightweight radial knowledge graph (root -> tags -> endpoints) rendered as SVG.
export const KnowledgeGraph = ({ graph, activeIndex = -1 }) => {
  const layout = useMemo(() => {
    if (!graph?.nodes?.length) return null;
    const W = 640, H = 420, cx = W / 2, cy = H / 2;
    const pos = {};
    const tags = graph.nodes.filter((n) => n.group === "tag");
    const endpoints = graph.nodes.filter((n) => n.group === "endpoint");

    pos["root"] = { x: cx, y: cy };
    tags.forEach((t, i) => {
      const a = (i / Math.max(tags.length, 1)) * Math.PI * 2 - Math.PI / 2;
      pos[t.id] = { x: cx + Math.cos(a) * 120, y: cy + Math.sin(a) * 120 };
    });
    endpoints.forEach((e, i) => {
      const a = (i / Math.max(endpoints.length, 1)) * Math.PI * 2 - Math.PI / 2;
      const r = 175 + (i % 3) * 22;
      pos[e.id] = { x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r };
    });
    return { W, H, pos, endpoints };
  }, [graph]);

  if (!layout) {
    return (
      <div className="flex h-full min-h-[320px] items-center justify-center text-sm text-muted-foreground">
        Awaiting discovery…
      </div>
    );
  }

  const { W, H, pos } = layout;
  const activeId = layout.endpoints[activeIndex]?.id;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-full w-full" data-testid="knowledge-graph">
      {graph.links.map((l, i) => {
        const s = pos[l.source], t = pos[l.target];
        if (!s || !t) return null;
        const hot = l.target === activeId;
        return (
          <motion.line
            key={i}
            x1={s.x} y1={s.y} x2={t.x} y2={t.y}
            stroke={hot ? "hsl(var(--primary))" : "hsl(var(--border))"}
            strokeWidth={hot ? 2 : 1}
            initial={{ opacity: 0 }}
            animate={{ opacity: hot ? 1 : 0.5 }}
            transition={{ duration: 0.4, delay: Math.min(i * 0.01, 0.5) }}
          />
        );
      })}
      {graph.nodes.map((n, i) => {
        const p = pos[n.id];
        if (!p) return null;
        const isRoot = n.group === "root";
        const isTag = n.group === "tag";
        const hot = n.id === activeId;
        const r = isRoot ? 16 : isTag ? 8 : 5;
        const fill = isRoot
          ? "hsl(var(--primary))"
          : isTag
          ? "hsl(217 60% 55%)"
          : hot
          ? "#22d3ee"
          : "hsl(var(--muted-foreground))";
        return (
          <motion.g
            key={n.id}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 260, damping: 20, delay: Math.min(i * 0.015, 0.6) }}
          >
            {hot && <circle cx={p.x} cy={p.y} r={r + 7} fill="#22d3ee" opacity={0.25} className="pulse-dot" />}
            <circle cx={p.x} cy={p.y} r={r} fill={fill} />
            {(isRoot || isTag) && (
              <text
                x={p.x} y={p.y - r - 6} textAnchor="middle"
                className="font-mono"
                fontSize={isRoot ? 12 : 10}
                fill="hsl(var(--foreground))"
              >
                {n.label?.slice(0, 22)}
              </text>
            )}
          </motion.g>
        );
      })}
    </svg>
  );
};
